import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { WmLineChart } from "@watermarkinsights/ripple-react";
import { useCounter, useDeepCompareEffect } from "react-use";
import Widget from "../../UI/Widget/Widget";
import WidgetHeader from "../../UI/Widget/WidgetHeader";
import WidgetRefreshButton from "./WidgetRefreshButton";
import StudentMetricsSelector from "../Modals/StudentMetricsSelector";
import { AlertError, AlertInfo } from "../../UI/StandardAlerts/StandardAlerts";
const CHART_TYPE_CONFIGS = {
    retention: {
        title: "Student Retention",
        yAxisLabel: "Student Retention Rate",
        ariaLabel: "Student Retention Chart",
        dataProperty: "retentionRate",
    },
    persistence: {
        title: "Student Persistence",
        yAxisLabel: "Student Persistence Rate",
        ariaLabel: "Student Persistence Chart",
        dataProperty: "persistenceRate",
    },
    completion: {
        title: "Course Completion",
        yAxisLabel: "Course Completion Rate",
        ariaLabel: "Student Retention Chart",
        dataProperty: "courseCompletionRate",
    },
};
const MetricChart = (props) => {
    var _a, _b, _c;
    const population = `${props.attributes.populationType.name}, ${props.attributes.studentType.name}, ${props.attributes.termType.id}`;
    // Changing key value whenever attributes(chart filters) changes to force refresh to avoid but from ripple component WmLineChart
    const [keyValue, { inc }] = useCounter(0);
    useDeepCompareEffect(() => {
        inc(1);
    }, [props.attributes]);
    const filteredDtos = props.data
        .filter((dto) => {
        if (props.populationOnly) {
            return (dto.populationId === props.attributes.populationType.id &&
                dto.studentTypeId === 0 &&
                dto.cohortType === "FALL");
        }
        return (dto.populationId === props.attributes.populationType.id &&
            dto.studentTypeId === props.attributes.studentType.id &&
            dto.cohortType === props.attributes.termType.id);
    })
        .filter((dto) => dto[props.dataProperty] !== null); // filter out null values
    if (!filteredDtos.length) {
        return _jsx(AlertInfo, { message: "No data" });
    }
    const lineData = JSON.stringify({
        [population]: filteredDtos.reduce((sum, dto) => (Object.assign(Object.assign({}, sum), { [dto.cohortName]: dto[props.dataProperty] * 100 })), {}),
    });
    // the highlighting should start immediately before the first instance of isImpactTerm
    // because isImpactTerm is signifying that a drastic change has occurred
    // between the previous term and the isImpactTerm term
    // find the last instance of isImpactTerm: false and return the cohortName
    let highlightStart = "";
    if (filteredDtos.some((dto) => dto.isImpactTerm)) {
        highlightStart =
            (_b = (_a = filteredDtos[filteredDtos.map((dto) => dto.isImpactTerm).lastIndexOf(false)]) === null || _a === void 0 ? void 0 : _a.cohortName) !== null && _b !== void 0 ? _b : "";
    }
    /**
     * Get a more human-readable label for the y-axis depending on the chart type
     */
    const yAxisLabel = (_c = CHART_TYPE_CONFIGS[props.type].yAxisLabel) !== null && _c !== void 0 ? _c : props.type;
    return (_jsx(WmLineChart, { lineData: lineData, xAxisLabel: "Term", yAxisLabel: yAxisLabel, labelWidth: "100px", yRange: "0,20,40,60,80,100", units: "YoY,%", highlightStart: highlightStart, showDeltas: true, highlightQualifier: "SS&E impact term", label: props.chartAriaLabel }, keyValue));
};
const StudentMetricsChartWidget = (props) => {
    var _a, _b, _c;
    const [attributes, setAttributes] = React.useState({
        populationType: {
            id: 0,
            name: "All Populations",
        },
        studentType: {
            id: 0,
            name: "All Student Types",
        },
        termType: {
            id: "FALL",
            name: "",
        },
    });
    /**
     * Get a more human-readable title, aria-label and the correct dataProperty being displayed.
     */
    const chartProps = {
        title: (_a = CHART_TYPE_CONFIGS[props.type].title) !== null && _a !== void 0 ? _a : props.type,
        ariaLabel: (_b = CHART_TYPE_CONFIGS[props.type].ariaLabel) !== null && _b !== void 0 ? _b : props.type,
        dataProperty: (_c = CHART_TYPE_CONFIGS[props.type].dataProperty) !== null && _c !== void 0 ? _c : props.type,
    };
    return (_jsxs(Widget, { children: [_jsx(WidgetHeader, { title: chartProps.title, children: props.chartState.data && (_jsx("div", { className: "text-nowrap ml-2", children: _jsx(WidgetRefreshButton, { isLoading: props.chartState.isLoading, onRefresh: props.refresh }) })) }), _jsx("div", { className: "mx-3 border-top", children: _jsxs("div", { className: "pt-3", children: [props.chartState.wasSuccessful && !props.chartState.data && (_jsx(AlertInfo, { message: "There is no data for this chart at this time" })), props.chartState.hasError && (_jsx(AlertError, { message: "There was an error loading this chart" })), props.chartState.data && (_jsx(StudentMetricsSelector, { chartState: props.chartState, populationOnly: props.type === "retention", attributes: attributes, setAttributes: setAttributes })), props.chartState.data && (_jsx(MetricChart, { data: props.chartState.data, dataProperty: chartProps.dataProperty, attributes: attributes, populationOnly: props.type === "retention", chartAriaLabel: chartProps.ariaLabel, type: props.type }))] }) })] }));
};
export default StudentMetricsChartWidget;
